import { supabase } from '../supabase';
import { createAuditLog } from './auditLogs';

export interface RbacRole {
  id: string;
  name: string;
  description: string | null;
  parent_id: string | null;
  is_system: boolean;
  created_at: string;
  updated_at: string;
}

export interface RbacPermission {
  id: string;
  action: string;
  description: string | null;
  module: string;
}

export interface RoleWithParent extends RbacRole {
  parent?: RbacRole | null;
  permission_count?: number;
}

export async function fetchMyPermissions(): Promise<string[]> {
  const { data, error } = await supabase.rpc('get_my_permissions');
  if (error) {
    console.error('Failed to fetch permissions:', error);
    return [];
  }
  return (data as Array<{ action: string }>).map(r => r.action);
}

export async function fetchAllRoles(): Promise<RoleWithParent[]> {
  const { data, error } = await supabase
    .from('rbac_roles')
    .select('*')
    .order('name');
  if (error) throw error;

  const roles = data as RbacRole[];

  const { data: rpData } = await supabase
    .from('rbac_role_permissions')
    .select('role_id');

  const countMap: Record<string, number> = {};
  if (rpData) {
    for (const rp of rpData) {
      countMap[rp.role_id] = (countMap[rp.role_id] || 0) + 1;
    }
  }

  return roles.map(r => ({
    ...r,
    parent: roles.find(p => p.id === r.parent_id) || null,
    permission_count: countMap[r.id] || 0,
  }));
}

export async function fetchAllPermissions(): Promise<RbacPermission[]> {
  const { data, error } = await supabase
    .from('rbac_permissions')
    .select('*')
    .order('module')
    .order('action');
  if (error) throw error;
  return data as RbacPermission[];
}

export async function fetchRolePermissions(roleId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from('rbac_role_permissions')
    .select('rbac_permissions(action)')
    .eq('role_id', roleId);
  if (error) throw error;
  return (data as unknown as Array<{ rbac_permissions: { action: string } | null }>)
    .map(r => r.rbac_permissions?.action ?? '')
    .filter(Boolean);
}

export async function fetchAllRolePermissionsResolved(roleId: string): Promise<string[]> {
  const { data, error } = await supabase.rpc('get_all_role_permissions', { p_role_id: roleId });
  if (error) throw error;
  return (data as Array<{ action: string }>).map(r => r.action);
}

export async function createRole(
  name: string,
  description: string,
  parentId: string | null
): Promise<RbacRole> {
  const { data, error } = await supabase
    .from('rbac_roles')
    .insert({ name, description, parent_id: parentId })
    .select()
    .single();
  if (error) throw error;
  return data as RbacRole;
}

export async function updateRole(
  id: string,
  updates: { name?: string; description?: string; parent_id?: string | null }
): Promise<void> {
  const { data: oldRole } = await supabase.from('rbac_roles').select('name, description, parent_id').eq('id', id).maybeSingle();
  const { error } = await supabase
    .from('rbac_roles')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
  const userId = (await supabase.auth.getUser()).data.user?.id;
  if (userId) {
    createAuditLog(userId, { action: 'role_updated', module: 'Admin', entity_type: 'rbac_roles', entity_id: id, old_values: oldRole, new_values: updates }).catch(() => {});
  }
}

export async function deleteRole(id: string): Promise<void> {
  const { data: oldRole } = await supabase.from('rbac_roles').select('name, description').eq('id', id).maybeSingle();
  const { error } = await supabase
    .from('rbac_roles')
    .delete()
    .eq('id', id)
    .eq('is_system', false);
  if (error) throw error;
  const userId = (await supabase.auth.getUser()).data.user?.id;
  if (userId) {
    createAuditLog(userId, { action: 'role_deleted', module: 'Admin', entity_type: 'rbac_roles', entity_id: id, old_values: oldRole }).catch(() => {});
  }
}

export async function setRolePermissions(
  roleId: string,
  permissionActions: string[]
): Promise<void> {
  const { data: allPerms, error: permErr } = await supabase
    .from('rbac_permissions')
    .select('id, action')
    .in('action', permissionActions);
  if (permErr) throw permErr;

  const permIds = (allPerms as RbacPermission[]).map(p => p.id);

  const { error: delErr } = await supabase
    .from('rbac_role_permissions')
    .delete()
    .eq('role_id', roleId);
  if (delErr) throw delErr;

  if (permIds.length === 0) return;

  const { error: insErr } = await supabase
    .from('rbac_role_permissions')
    .insert(permIds.map(pid => ({ role_id: roleId, permission_id: pid })));
  if (insErr) throw insErr;

  const userId = (await supabase.auth.getUser()).data.user?.id;
  if (userId) {
    createAuditLog(userId, { action: 'role_permissions_changed', module: 'Admin', entity_type: 'rbac_roles', entity_id: roleId, new_values: { permissions: permissionActions } }).catch(() => {});
  }
}

export async function assignRbacRoleToProfile(
  profileId: string,
  rbacRoleId: string
): Promise<void> {
  const { data: oldAssignment } = await supabase.from('profile_rbac_role').select('rbac_role_id').eq('profile_id', profileId).maybeSingle();
  const { error } = await supabase
    .from('profile_rbac_role')
    .upsert({ profile_id: profileId, rbac_role_id: rbacRoleId }, { onConflict: 'profile_id' });
  if (error) throw error;
  const userId = (await supabase.auth.getUser()).data.user?.id;
  if (userId) {
    createAuditLog(userId, { action: 'rbac_role_assigned', module: 'Admin', entity_type: 'profiles', entity_id: profileId, old_values: oldAssignment ? { rbac_role_id: oldAssignment.rbac_role_id } : null, new_values: { rbac_role_id: rbacRoleId } }).catch(() => {});
  }
}

export async function fetchProfileRbacRole(profileId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('profile_rbac_role')
    .select('rbac_role_id')
    .eq('profile_id', profileId)
    .maybeSingle();
  if (error) return null;
  return data?.rbac_role_id ?? null;
}
